import type { Step } from "react-joyride";
import { onboardingTargetSelector } from "../onboardingSelectors";
import type { BuildOnboardingSteps } from "./types";

/**
 * Resolves once the browser has committed and painted a frame, giving a tour
 * step's `before` side effect time to mount its conditionally-rendered target.
 */
const waitForRender = (): Promise<void> =>
  new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });

/** Walks every scrollable ancestor — Joyride's built-in scroll only handles the page. */
const scrollStepTargetIntoView = (selector: string): void => {
  const target = document.querySelector<HTMLElement>(selector);
  target?.scrollIntoView({ block: "center", behavior: "auto" });
};

// Category steps that teach editing actions hidden from read-only members.
const EDIT_ONLY_CATEGORY_STEP_IDS = new Set([
  "groupName",
  "elementDetails",
  "approvalCheckbox",
  "topActions",
]);

export const buildOnboardingSteps: BuildOnboardingSteps = ({
  t,
  canEdit,
  canDelete,
  hasDiagrams,
  hasElements,
  onShowElementsTab,
  onRevealFirstCategory,
}) => {
  const tr = (key: string): string =>
    t(`smartSurveyor.onboarding.steps.${key}`);

  const layoutSteps: Step[] = [
    {
      id: "welcome",
      target: "body",
      placement: "center",
      buttons: ["skip", "primary"],
      title: tr("welcome.title"),
      content: tr("welcome.body"),
    },
    {
      id: "sidebarTabs",
      target: onboardingTargetSelector("sidebarTabs"),
      placement: "right",
      title: tr("sidebarTabs.title"),
      content: hasDiagrams
        ? tr("sidebarTabs.bodyWithDiagrams")
        : tr("sidebarTabs.bodyPlansOnly"),
    },
    {
      id: "planList",
      target: onboardingTargetSelector("planList"),
      placement: "right",
      title: tr("planList.title"),
      content: !canEdit
        ? tr("planList.bodyReadOnly")
        : canDelete
          ? tr("planList.bodyWithDelete")
          : tr("planList.body"),
    },
    {
      id: "viewer",
      target: onboardingTargetSelector("viewer"),
      placement: "right",
      title: tr("viewer.title"),
      content: tr("viewer.body"),
    },
    {
      id: "rightTabs",
      target: onboardingTargetSelector("rightTabs"),
      placement: "left",
      title: tr("rightTabs.title"),
      content: tr("rightTabs.body"),
      before: async () => {
        onShowElementsTab();
        await waitForRender();
      },
    },
  ];

  const headerActionsStep: Step = {
    id: "headerActions",
    target: onboardingTargetSelector("headerActions"),
    placement: "bottom",
    title: tr("headerActions.title"),
    content: canEdit
      ? tr("headerActions.body")
      : tr("headerActions.bodyReadOnly"),
  };

  const finishStep: Step = {
    id: "finish",
    target: "body",
    placement: "center",
    buttons: ["back", "primary"],
    title: tr("finish.title"),
    content: tr("finish.body"),
  };

  if (!hasElements) {
    return [...layoutSteps, headerActionsStep, finishStep];
  }

  const allCategorySteps: Step[] = [
    {
      id: "categoryList",
      target: onboardingTargetSelector("categoryList"),
      placement: "left",
      title: tr("categoryList.title"),
      content: tr("categoryList.body"),
      before: async () => {
        onShowElementsTab();
        await waitForRender();
      },
    },
    {
      id: "categoryDetail",
      target: onboardingTargetSelector("inspectorHeader"),
      placement: "left",
      title: tr("categoryDetail.title"),
      content: tr("categoryDetail.body"),
      before: async () => {
        onRevealFirstCategory();
        await waitForRender();
      },
    },
    {
      id: "startReview",
      target: onboardingTargetSelector("inspectorStartReviewButton"),
      placement: "left",
      title: tr("startReview.title"),
      content: tr("startReview.body"),
      blockTargetInteraction: false,
      buttons: ["back", "skip"],
    },
    {
      id: "groupName",
      target: onboardingTargetSelector("inspectorGroupName"),
      placement: "left",
      title: tr("groupName.title"),
      content: tr("groupName.body"),
    },
    {
      id: "elementDetails",
      target: onboardingTargetSelector("elementDetails"),
      placement: "left",
      title: tr("elementDetails.title"),
      content: tr("elementDetails.body"),
      before: async () => {
        scrollStepTargetIntoView(onboardingTargetSelector("elementDetails"));
        await waitForRender();
      },
    },
    {
      id: "approvalCheckbox",
      target: `${onboardingTargetSelector("categoryRowFirst")} input[type="checkbox"]`,
      placement: "left",
      title: tr("approvalCheckbox.title"),
      content: tr("approvalCheckbox.body"),
    },
    {
      id: "topActions",
      target: onboardingTargetSelector("approvalBar"),
      placement: "left",
      title: tr("topActions.title"),
      content: tr("topActions.body"),
    },
  ];

  const categorySteps = canEdit
    ? allCategorySteps
    : allCategorySteps.filter(
        (step) => !EDIT_ONLY_CATEGORY_STEP_IDS.has(step.id as string),
      );

  const searchStep: Step = {
    id: "search",
    target: onboardingTargetSelector("searchInput"),
    placement: "left",
    title: tr("search.title"),
    content: tr("search.body"),
  };

  return [
    ...layoutSteps,
    ...categorySteps,
    searchStep,
    headerActionsStep,
    finishStep,
  ];
};
